import React, { useEffect, useState } from "react";
import SideBar from "../components/side-bar";
import Header from "../components/header";
import Footer from "../components/footer";
import { IsStringNullEmptyOrUndefined } from "../utils/null-check";
import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { AssignedModuleData, UserInfoEntity } from "../interface/sign-in";
import { ActionEntityEnum, RibbonEnumEntity, StorageKeyEnum } from "../utils/enum";
import { allFeaturedModules } from "../utils/constants";
import { useRedRibbon } from "../store/redRibbon.store";
import { useBreadCrumb } from "../store/breadcrumb.store";
import { BreadCrumbItem } from "../components/bread-crumb";
declare var $: any;

/*
  Breadcrumb trail to prepend before the page's own leaf label (which
  always comes free from `featuredPage` below — every route already passes
  it for its own permission check, so there's nothing extra to register
  just to get a leaf crumb).

  Zero-config default: any route under /settings/... gets "Settings"
  prepended automatically, covering the large majority of settings pages
  with no per-route setup at all.

  For a page nested one level deeper — a hub living inside another hub,
  e.g. the Production pages living inside Settings — pass `breadcrumbTrail`
  directly on that route's <AuthAppLayout> in routes.tsx instead of editing
  this file. See the settings/production/* routes there for the exact
  pattern to copy onto a new sibling page.
*/
const DEFAULT_BREADCRUMB_TRAIL: { prefix: string; items: BreadCrumbItem[] }[] = [
  { prefix: '/settings/', items: [{ label: 'Settings', url: '/settings' }] },
];

// Pages that don't want a breadcrumb at all (e.g. the dashboard IS home).
const BREADCRUMB_SKIP_FEATURED_PAGES: string[] = [allFeaturedModules.dashboard];

function AuthAppLayout({
  page,
  featuredPage,
  breadcrumbTrail,
  breadcrumbLeaf,
}: {
  page: JSX.Element;
  featuredPage: string;
  // Either a fixed trail, or a builder run with the CURRENT route's path
  // params — needed when a create/edit page is reached from a specific
  // record's own manage/detail page (not the generic module hub), e.g.
  // .../create-wall-order-solution/:orderId is opened FROM
  // .../manage-wall-order-solution/:orderId for that same order, so its
  // trail must link to that exact order's manage page, not a static url:
  //   breadcrumbTrail={(params) => [WALL_ORDERS_HUB,
  //     { label: 'Manage Solutions', url: `/manage-wall-order-solution/${params.orderId}` }]}
  breadcrumbTrail?: BreadCrumbItem[] | ((params: Readonly<Record<string, string | undefined>>) => BreadCrumbItem[]);
  // Overrides the final crumb's text (defaults to featuredPage). Needed
  // whenever several distinct pages share one featuredPage (it doubles as
  // the permission-check module name, so it's often generic/repeated) —
  // e.g. every Wall Orders sub-page shares featuredPage="Waltz Orders", so
  // each passes its own breadcrumbLeaf ("Manage Order", "Ledger Form", ...)
  // instead of all showing the same crumb.
  //
  // Also a builder form for the common "one route does both create and
  // update, told apart by a query param" pages, e.g.:
  //   breadcrumbLeaf={(_, search) => search.get('id') ? 'Update System' : 'Create System'}
  breadcrumbLeaf?: string | ((params: Readonly<Record<string, string | undefined>>, searchParams: URLSearchParams) => string);
}) {
  const [isauthorized, setIsauthorized] = useState<number>(-1);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  let { count } = useRedRibbon();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { setAutoBreadCrumb } = useBreadCrumb();

  useEffect(() => {
    if (!featuredPage || IsStringNullEmptyOrUndefined(featuredPage) || BREADCRUMB_SKIP_FEATURED_PAGES.includes(featuredPage)) {
      setAutoBreadCrumb([]);
      return;
    }

    const resolvedTrail = typeof breadcrumbTrail === 'function' ? breadcrumbTrail(params) : breadcrumbTrail;
    const trail = resolvedTrail
      ?? DEFAULT_BREADCRUMB_TRAIL.find((s) => location.pathname.startsWith(s.prefix))?.items
      ?? [];
    const leaf = typeof breadcrumbLeaf === 'function' ? breadcrumbLeaf(params, searchParams) : (breadcrumbLeaf ?? featuredPage);
    setAutoBreadCrumb([...trail, { label: leaf }]);

    return () => setAutoBreadCrumb([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredPage, location.pathname, location.search, breadcrumbTrail, breadcrumbLeaf]);
  useEffect(() => {
    $(function () {
      $('[data-toggle="tooltip"]').tooltip();
    });
  }, []);
  useEffect(() => {
    if (featuredPage && !IsStringNullEmptyOrUndefined(featuredPage)) {
      const userStr: string | null = localStorage.getItem(
        StorageKeyEnum.JB_GLASS_USER_INFO
      );
      if (userStr && !IsStringNullEmptyOrUndefined(userStr)) {
        const userInfo: UserInfoEntity = JSON.parse(userStr);
        const modules: AssignedModuleData[] = userInfo.assignedModule;
        if (featuredPage === allFeaturedModules.changePassword || featuredPage === allFeaturedModules.executionOrderDetail) {
          setIsauthorized(1);
        } else {
          const index = modules.findIndex(
            (e) =>
              e.moduleName === featuredPage &&
              e.isActive &&
              e.action === ActionEntityEnum.LIST
          );
          setIsauthorized(index !== -1 ? 1 : 403);
        }
      } else {
        setIsauthorized(0);
      }
    }
  }, [featuredPage]);

  useEffect(() => {
    if (featuredPage && !IsStringNullEmptyOrUndefined(featuredPage)) {
      const userStr: string | null = localStorage.getItem(
        StorageKeyEnum.JB_GLASS_USER_INFO
      );

      if (userStr && !IsStringNullEmptyOrUndefined(userStr)) {
        const userInfo: UserInfoEntity = JSON.parse(userStr);
        const modules: AssignedModuleData[] = userInfo.assignedModule;
        const foundModule: boolean = modules.some(
          (item) => item.moduleName === RibbonEnumEntity.RED_RIBBON
        ) && modules.some(
          (item) => item.moduleName === RibbonEnumEntity.COMMERCIAL_HOLD ||
            item.moduleName === RibbonEnumEntity.RR_PRODUCTION_HOLD_WO ||
            item.moduleName === RibbonEnumEntity.RR_TBS_HOLD_WO ||
            item.moduleName === RibbonEnumEntity.RR_PRODUCTION_PLANNED_WO ||
            item.moduleName === RibbonEnumEntity.RR_IN_PRODUCTION_WO ||
            item.moduleName === RibbonEnumEntity.RR_DISPATCH_HOLD_WO ||
            item.moduleName === RibbonEnumEntity.RR_READY_FOR_DISPATCH_WO ||
            item.moduleName === RibbonEnumEntity.RR_DISPACTH_PLANNED_WO ||
            item.moduleName === RibbonEnumEntity.RR_DELIVERED_WOS ||
            item.moduleName === RibbonEnumEntity.SHOP_DRAWING_APPROVAL_PENDING_ORDER ||
            item.moduleName === RibbonEnumEntity.SHOP_DRAWING_AWAITING_ORDER ||
            item.moduleName === RibbonEnumEntity.NOT_FEASIBLE_ORDERS ||
            item.moduleName === RibbonEnumEntity.SHOP_DRAWING_QUERY_PENDING_ORDER ||
            item.moduleName === RibbonEnumEntity.SITE_NOT_READY_PENDING_ORDERS ||
            item.moduleName === RibbonEnumEntity.MEASUREMENT_SHEET_PENDING_ORDER ||
            item.moduleName === RibbonEnumEntity.MEASUREMENT_SHEET_QUERY_PENDING_ORDER ||
            item.moduleName === RibbonEnumEntity.HANDOVER_PENDING ||
            item.moduleName === RibbonEnumEntity.RR_POLICY_EXPIRY_OR_REMAINING_AMOUNT
        );

        if (foundModule) {
          setIsOpen(true);
        }
      }
    }
  }, []);

  return (
    <>
      {isauthorized === 1 && (
        <div className="container-scroller">
          <Header />
          <div
            className={`container-fluid page-body-wrapper ${(isOpen && count) ? "padTop112" : ""
              }`}
          >
            <SideBar />
            <div className="main-panel">
              {page}
              <Footer />
            </div>
          </div>
        </div>
      )}
      {isauthorized === 403 && <Navigate replace to={"/forbidden"} />}
      {isauthorized === 0 && <Navigate replace to={"/"} />}
    </>
  );
}
export default AuthAppLayout;